import { supabase } from './supabase'

// Shared revision routing for the 4-phase blog and social workflows.
// Each config maps the generic roles (work / review / client) onto that
// workflow's column names and phase values.
export const BLOG_FLOW = {
  parentTable: 'blog_assignments',
  parentKey: 'blog_assignment_id',
  phaseKey: 'blog_phase',
  workPhase: 'draft',
  reviewPhase: 'review',
  clientPhase: 'client_review',
  workerLabel: 'copywriter',
}

export const SOCIAL_FLOW = {
  parentTable: 'social_batches',
  parentKey: 'social_batch_id',
  phaseKey: 'social_phase',
  workPhase: 'build',
  reviewPhase: 'review',
  clientPhase: 'client_review',
  workerLabel: 'creator',
}

// Called when the AD (review step) or the client (client step) asks for
// revisions. Sends the work back to the copywriter/creator so it shows up in
// THEIR tasks as Needs Revision, with the note as a comment and a notification.
//   source: 'internal' (from review step) | 'client' (from client step)
// Returns { error } — error is null on success.
export async function sendRevisionToWorker(flow, task, note, currentUserId, source) {
  const parentId = task[flow.parentKey]
  if (!parentId) return { error: new Error('Task is not linked to a workflow') }

  const { data: worker, error: findError } = await supabase.from('tasks')
    .select('id, title, assigned_to')
    .eq(flow.parentKey, parentId)
    .eq(flow.phaseKey, flow.workPhase)
    .maybeSingle()
  if (findError) return { error: findError }
  if (!worker) return { error: new Error(`Could not find the ${flow.workerLabel}'s task`) }

  const { error: updError } = await supabase.from('tasks')
    .update({ status: 'needs_revision' }).eq('id', worker.id)
  if (updError) return { error: updError }

  const prefix = source === 'client' ? '↩ Client requested revisions' : '↩ Revision requested'
  await supabase.from('task_comments').insert({
    task_id: worker.id,
    author_id: currentUserId,
    content: `${prefix}: ${note}`,
    mentions: worker.assigned_to ? [worker.assigned_to] : [],
  })

  if (worker.assigned_to && worker.assigned_to !== currentUserId) {
    await supabase.from('notifications').insert({
      user_id: worker.assigned_to,
      task_id: worker.id,
      type: 'needs_revision',
      message: source === 'client'
        ? `The client requested revisions on "${worker.title}"`
        : `"${worker.title}" needs revisions`,
    })
  }

  // Client revisions also re-open the AD's review step, so the revised work is
  // checked internally again before it goes back to the client.
  if (source === 'client') {
    await supabase.from('tasks').update({ status: 'needs_revision' })
      .eq(flow.parentKey, parentId).eq(flow.phaseKey, flow.reviewPhase)
  }

  // Parent workflow goes back to the writing/building phase.
  await supabase.from(flow.parentTable).update({ status: 'drafting' }).eq('id', parentId)

  return { error: null }
}

// Called when the copywriter/creator resubmits. If the review step was waiting
// on revisions, put it back in front of the AD as Ready for Review.
export async function reopenReviewAfterResubmit(flow, task) {
  const parentId = task[flow.parentKey]
  if (!parentId) return
  await supabase.from('tasks').update({ status: 'ready_for_review' })
    .eq(flow.parentKey, parentId)
    .eq(flow.phaseKey, flow.reviewPhase)
    .eq('status', 'needs_revision')
}
